import React, { Dispatch, ReactElement, SetStateAction, useEffect, useState } from 'react';
import { NavigateFunction, Params, useNavigate, useParams } from 'react-router-dom';

import { ARCHIVED, UPCOMING } from '../../../../utils/constants';
import { checkForTribeChanges } from '../../../../utils/diff-util';

import { RadioOrSelectOption } from '../../../../types/common.types';
import {
  HashTag,
  Member,
  MemberProfile,
  Post,
  PostWithHierarchy,
  SearchResult,
  TribeTemplate
} from '../../../../types/entity.types';

import ProgrammeLayout from '../../../../layouts/programme/ProgrammeLayout';
import SaveChangesModal from '../../../common/modal/save-changes-modal/SaveChangesModal';
import TribeContent from './tribe-content/TribeContent';

import { ReactComponent as SaveIcon } from '../../../../resources/icons/save.svg';
import { ReactComponent as GearIcon } from '../../../../resources/icons/gear.svg';
import { ReactComponent as CloseIcon } from '../../../../resources/icons/close.svg';

import './tribe.scss';
import { noOp } from '../../../../utils/util';

type Props = {
  status: string;
  tribeTemplate?: TribeTemplate;
  pendingApprovals?: number;
  posts?: Post[];
  memberPosts?: Post[];
  members?: Member[];
  hashtags?: HashTag[];
  profilePicture?: string;
  searchType?: RadioOrSelectOption;
  createHashtag: () => void;
  updateHashtag: (id: string | number, value: string) => void;
  deleteHashtag: (id: string | number) => void;
  createTribeTemplate: (template: TribeTemplate, url: string | undefined) => void;
  updateTribeTemplate: (template: TribeTemplate, url: string | undefined) => void;
  loadPosts: () => void;
  refreshPost: (postExternalReference: string) => void;
  clearPost: () => void;
  setSearchType: Dispatch<SetStateAction<RadioOrSelectOption>>;
  executeSearch: (searchValue: string) => void;
  postWithHierarchy?: PostWithHierarchy;
  searchResults?: SearchResult[];
  clearPostSearchResults: () => void;
  loadPostWithHierarchy?: (externalReference: string) => void;
  profile?: MemberProfile;
};

const Tribe = ({
  status,
  tribeTemplate,
  pendingApprovals = 0,
  posts = [],
  memberPosts = [],
  members = [],
  hashtags = [],
  profilePicture,
  searchType,
  createHashtag,
  updateHashtag,
  deleteHashtag,
  createTribeTemplate,
  updateTribeTemplate,
  loadPosts,
  refreshPost,
  clearPost,
  setSearchType,
  searchResults,
  clearPostSearchResults,
  executeSearch,
  loadPostWithHierarchy = noOp,
  postWithHierarchy,
  profile
}: Props): ReactElement => {
  const [settingsOpen, setSettingsOpen] = useState<boolean>(false);
  const [redirectUrl, setRedirectUrl] = useState<string | undefined>(undefined);
  const [approvePosts, setApprovePosts] = useState<boolean>(false);
  const [approvePhotos, setApprovePhotos] = useState<boolean>(false);
  const [approveComments, setApproveComments] = useState<boolean>(false);

  const navigate: NavigateFunction = useNavigate();

  const { memberExternalReference }: Readonly<Params> = useParams();

  useEffect((): void => {
    if (tribeTemplate) {
      setApprovePosts(tribeTemplate.approvePosts ?? false);
      setApprovePhotos(tribeTemplate.approvePhotos ?? false);
      setApproveComments(tribeTemplate.approveComments ?? false);
    }
  }, [tribeTemplate]);

  const checkForUnsavedChanges = (url: string): void => {
    const tribeTemplateToSave: TribeTemplate = {
      ...tribeTemplate,
      approvePosts,
      approvePhotos,
      approveComments,
      hashTags: hashtags.map((hashtag: HashTag): HashTag => ({ text: removeSpacesFromHashTag(hashtag.name ?? '') }))
    };

    if (checkForTribeChanges(tribeTemplate, tribeTemplateToSave)) {
      setRedirectUrl(url);
    } else {
      navigate(url);
    }
  };

  const removeSpacesFromHashTag = (hashtag: string): string => {
    return hashtag.replace(/\s/g, '');
  };

  const handleSaveClick = (): void => {
    const tribeTemplateToSave: TribeTemplate = {
      approvePosts,
      approvePhotos,
      approveComments,
      hashTags: hashtags.map((hashtag: HashTag): HashTag => ({ text: removeSpacesFromHashTag(hashtag.name ?? '') }))
    };

    if (tribeTemplate?.externalReference) {
      updateTribeTemplate(
        {
          externalReference: tribeTemplate.externalReference,
          ...tribeTemplateToSave
        },
        redirectUrl
      );
    } else {
      createTribeTemplate(tribeTemplateToSave, redirectUrl);
    }
  };

  return (
    <ProgrammeLayout onNavigate={checkForUnsavedChanges}>
      <div className="tribe" data-testid={`tribe-${status}`}>
        <div className="tribe__icons">
          {((settingsOpen && status !== ARCHIVED) || status === UPCOMING) && (
            <div className="tribe__icon-container" onClick={handleSaveClick}>
              <SaveIcon className="tribe__save-icon" data-testid="save-icon" />
            </div>
          )}
          {!settingsOpen && status !== UPCOMING && (
            <div className="tribe__icon-container" onClick={(): void => setSettingsOpen(true)}>
              <GearIcon className="tribe__gear-icon" data-testid="cog-icon" />
            </div>
          )}
          {settingsOpen && status !== UPCOMING && (
            <div className="tribe__icon-container" onClick={(): void => setSettingsOpen(false)}>
              <CloseIcon className="tribe__close-icon" data-testid="close-icon" />
            </div>
          )}
        </div>

        <TribeContent
          status={status}
          settingsOpen={settingsOpen}
          searchResults={searchResults}
          postWithHierarchy={postWithHierarchy}
          memberExternalReference={memberExternalReference}
          profilePicture={profilePicture}
          members={members}
          pendingApprovals={pendingApprovals}
          searchType={searchType}
          posts={posts}
          hashtags={hashtags}
          profile={profile}
          memberPosts={memberPosts}
          approvePosts={approvePosts}
          approvePhotos={approvePhotos}
          approveComments={approveComments}
          setSearchType={setSearchType}
          loadPostWithHierarchy={loadPostWithHierarchy}
          loadPosts={loadPosts}
          refreshPost={refreshPost}
          setApprovePosts={setApprovePosts}
          setApprovePhotos={setApprovePhotos}
          setApproveComments={setApproveComments}
          createHashtag={createHashtag}
          updateHashtag={updateHashtag}
          deleteHashtag={deleteHashtag}
          clearPost={clearPost}
          clearPostSearchResults={clearPostSearchResults}
          executeSearch={executeSearch}
        />
      </div>

      <SaveChangesModal redirectUrl={redirectUrl} handleSaveClick={handleSaveClick} />
    </ProgrammeLayout>
  );
};

Tribe.defaultProps = {
  tribeTemplate: undefined,
  pendingApprovals: 0,
  posts: [],
  memberPosts: [],
  members: [],
  hashtags: [],
  profilePicture: undefined
};

export default Tribe;
